/**
 * Console shell (boards/Nav.dc.html): the sidebar carries the brand, the global
 * 「查一台機器」 button (Ctrl/Cmd+K), four nav groups with indented sub-pages, and the signed-in
 * user + theme toggle at the bottom. No topbar. Groups gate on the lowest role that may open
 * them; the route layer (ProtectedRoute) is the matching gate.
 */
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { LookupProvider, useLookup } from '@/contexts/LookupContext';
import { usePoll } from '@/lib/usePoll';
import { api } from '@/lib/api';
import { roleAtLeast } from '@/lib/roles';
import { ROLE_LABEL_ZH } from '@/lib/labels';
import { Icon } from './Icon';
import { ThemeToggle } from './ThemeToggle';
import type { Role, DashboardSummary } from '@/types';

type BadgeKey = keyof Pick<DashboardSummary, 'pending_admins' | 'open_tickets'>;
// `match` overrides NavLink's prefix matching: /roster/import belongs to 硬體序號名單 (board),
// /roster/batches does not.
type NavItem = { to: string; label: string; minRole: Role; sub?: boolean; badgeKey?: BadgeKey; match?: (path: string) => boolean };
type NavGroup = { label: string; items: NavItem[] };

const GROUPS: NavGroup[] = [
  {
    label: '觀測',
    items: [
      { to: '/dashboard', label: '總覽', minRole: 'read_only' },
      { to: '/insights', label: '營運洞察', minRole: 'read_only' },
      { to: '/audit', label: '稽核日誌', minRole: 'admin' },
    ],
  },
  {
    label: '授權',
    items: [
      { to: '/roster', label: '硬體序號名單', minRole: 'admin', match: (p) => p === '/roster' || p.startsWith('/roster/import') },
      { to: '/roster/batches', label: '匯入批次', minRole: 'admin', sub: true },
      { to: '/models', label: '支援機型', minRole: 'admin' },
      { to: '/licenses', label: '軟體序號', minRole: 'admin', match: (p) => p === '/licenses' },
      { to: '/licenses/batches', label: '序號批次', minRole: 'admin', sub: true },
    ],
  },
  {
    label: '客服',
    items: [{ to: '/support', label: '客服工單', minRole: 'support', badgeKey: 'open_tickets' }],
  },
  {
    label: '管理',
    items: [{ to: '/users', label: '使用者管理', minRole: 'super_admin', badgeKey: 'pending_admins' }],
  },
];

export function AppShell() {
  return (
    <LookupProvider>
      <Shell />
    </LookupProvider>
  );
}

function Shell() {
  const { email, role, signOut } = useAuth();
  const lookup = useLookup();
  const { pathname } = useLocation();

  // One aggregated call drives every sidebar attention badge; admin-api scopes it to the role.
  const { data: summary } = usePoll<DashboardSummary>(() => api.dashboardSummary(), 15_000);
  const badgeFor = (key?: BadgeKey): number | undefined => {
    if (!key || !summary) return undefined;
    const n = summary[key];
    return typeof n === 'number' && n > 0 ? n : undefined;
  };

  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
  const groups = GROUPS.map((g) => ({ ...g, items: g.items.filter((i) => roleAtLeast(role, i.minRole)) })).filter(
    (g) => g.items.length > 0,
  );

  return (
    <div className="app-shell">
      <aside className="sidebar" aria-label="主選單">
        <div className="brand">
          <div className="brand-mark ink">A</div>
          <div className="brand-text">
            <div className="brand-title">Armor 後台</div>
            <div className="brand-sub">管理員後台 v2</div>
          </div>
        </div>

        {lookup.enabled && (
          <button type="button" className="lookup-btn" onClick={() => lookup.open()}>
            <Icon name="search" size={14} />
            <span className="grow">查一台機器</span>
            <span className="kbd">{isMac ? '⌘ K' : 'Ctrl K'}</span>
          </button>
        )}

        <nav className="nav2">
          {groups.map((g) => (
            <div key={g.label} style={{ display: 'contents' }}>
              <div className="nav2-label">{g.label}</div>
              {g.items.map((n) => {
                const badge = badgeFor(n.badgeKey);
                return (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    className={({ isActive }) =>
                      'nav2-link' + (n.sub ? ' sub' : '') + ((n.match ? n.match(pathname) : isActive) ? ' active' : '')
                    }
                  >
                    <span className="grow">{n.label}</span>
                    {badge ? (
                      <span className="nav-badge" aria-label={`${badge} 待處理`}>{badge}</span>
                    ) : null}
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="sidebar-foot">
          <div className="avatar" aria-hidden="true">{(email ?? '?').slice(0, 1)}</div>
          <div className="who">
            <span className="email" title={email ?? ''}>{email ?? 'Unknown'}</span>
            <span className="role">{role ? ROLE_LABEL_ZH[role] : '—'}</span>
          </div>
          <ThemeToggle />
          <button type="button" className="icon-btn" onClick={signOut} title="登出" aria-label="登出">
            <Icon name="log-out" size={16} />
          </button>
        </div>
      </aside>

      <main className="main">
        <section className="content" role="main">
          <Outlet />
        </section>
      </main>
    </div>
  );
}
